import React from "react"

import { formatMoney } from "helpers/helpers"

export const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

export const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

export const CHART_COLORS = ["#4c8df0", "#1f9d57", "#f0a04c", "#9b5de5", "#d23434", "#00b8d9"]

/**
 * Пересчёт в доллары идёт по курсу СВОЕГО месяца, а не сегодняшнему: иначе годовой
 * график поедет на величину девальвации и покажет падение там, где его не было.
 * Курса за месяц нет — оставляем сумму в сумах (курсы наполняет cronCurrencyRates).
 */
export function convert(value, month, currency, rates) {
	const number = Number(value || 0)
	if (currency !== "usd") return number

	// У метрики может не быть месяца (текущий остаток, рейтинг за период) — тогда
	// берём самый свежий известный курс.
	let rate = Number(rates?.[month] || 0)
	if (!rate) {
		const months = Object.keys(rates || {}).sort()
		rate = months.length > 0 ? Number(rates[months[months.length - 1]] || 0) : 0
	}
	if (!rate) return number

	return number / rate
}

/** Крупные суммы пишем сокращённо: 160 190 000 000 не читается, «160,2 млрд» читается. */
export function formatAmount(value, currency = "uzs") {
	const number = Number(value || 0)
	const abs = Math.abs(number)

	if (currency === "usd") {
		if (abs >= 1000000) return "$" + (number / 1000000).toFixed(2) + " млн"
		if (abs >= 1000) return "$" + formatMoney(Math.round(number))
		return "$" + number.toFixed(2)
	}

	if (abs >= 1000000000) return (number / 1000000000).toFixed(2).replace(".00", "") + " млрд"
	if (abs >= 1000000) return (number / 1000000).toFixed(1).replace(".0", "") + " млн"

	return formatMoney(Math.round(number))
}

export function formatNumber(value) {
	return formatMoney(Math.round(Number(value || 0)))
}

export function formatPercent(value, digits = 1) {
	if (value === null || value === undefined) return "—"
	return Number(value).toFixed(digits).replace(/\.0$/, "") + " %"
}

export function formatGrowth(value) {
	if (value === null || value === undefined) return null
	const number = Number(value)
	return `${number >= 0 ? "+" : ""}${number.toFixed(1).replace(".0", "")}%`
}

export function GrowthBadge({ value, label }) {
	if (value === null || value === undefined) return null
	const number = Number(value)

	return (
		<span className="d-inline-flex align-items-center gap-1" style={{ fontSize: "11px" }}>
			<span
				style={{
					color: number >= 0 ? "var(--dark-fg-success, #1f9d57)" : "#d23434",
					background: number >= 0 ? "var(--dark-tint-success, #e7f8ef)" : "var(--dark-tint-danger, #fdecec)",
					borderRadius: "12px",
					padding: "2px 7px",
					fontWeight: 600,
				}}
			>
				{number >= 0 ? "↗" : "↘"} {formatGrowth(value)}
			</span>
			{label && <span className="text-muted">{label}</span>}
		</span>
	)
}

export function Card({ title, hint, right, children, className = "" }) {
	return (
		<div className={`card h-100 ${className}`} style={cardStyle}>
			<div className="card-body py-3 px-3">
				{(title || right) && (
					<div className="d-flex justify-content-between align-items-center mb-2">
						<div className="d-flex align-items-center gap-1" style={{ fontWeight: 600, fontSize: "13px" }}>
							{title}
							{hint}
						</div>
						{right}
					</div>
				)}
				{children}
			</div>
		</div>
	)
}

/** Скелетон рисуем внутри вкладки: шапка и переключатель периода остаются кликабельными. */
export function Skeleton({ rows = 3 }) {
	return (
		<div>
			{[...Array(rows)].map((item, index) => (
				<div
					key={index}
					className="mb-2"
					style={{
						height: index === 0 ? "90px" : "220px",
						borderRadius: "12px",
						background: "linear-gradient(90deg, var(--dark-surface-2, #f3f5f8) 25%, var(--dark-surface-3, #eaeef3) 37%, var(--dark-surface-2, #f3f5f8) 63%)",
						backgroundSize: "400% 100%",
						animation: "analyticsShimmer 1.4s ease infinite",
					}}
				></div>
			))}
			<style>{`@keyframes analyticsShimmer { 0% { background-position: 100% 50% } 100% { background-position: 0 50% } }`}</style>
		</div>
	)
}

/** Упавшая вкладка не должна ронять страницу — своя ошибка и своя кнопка «Повторить». */
export function TabError({ message, onRetry }) {
	return (
		<div className="card" style={cardStyle}>
			<div className="card-body text-center py-5">
				<i className="uil-exclamation-triangle" style={{ fontSize: "28px", color: "var(--dark-fg-danger, #d23434)" }}></i>
				<div className="mt-2 mb-3" style={{ color: "var(--dark-text-muted, #6b7280)" }}>{message || "Не удалось загрузить данные"}</div>
				<button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>Повторить</button>
			</div>
		</div>
	)
}

export function EmptyNote({ children }) {
	return (
		<div className="text-center py-4" style={{ color: "var(--dark-text-muted, #8791a1)", fontSize: "13px" }}>
			{children}
		</div>
	)
}

export const baseChartOptions = {
	chart: { toolbar: { show: false }, fontFamily: "inherit" },
	dataLabels: { enabled: false },
	grid: { borderColor: "#eef1f5", strokeDashArray: 4 },
	xaxis: {
		labels: { style: { colors: "#8791a1", fontSize: "11px" } },
		axisBorder: { show: false },
		axisTicks: { show: false },
	},
	yaxis: { labels: { style: { colors: "#8791a1", fontSize: "11px" } } },
	legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
	tooltip: { theme: "light" },
}
